import React, { useState } from "react";
import { Upload, Button, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd/es/upload";
import { useTranslation } from "react-i18next";
import { resizeImage } from "utils/utils";
import "./PhotoCapturePage.scss";

interface PhotoCapturePageProps {
  uploadedPhoto: UploadFile | null;
  onPhotoChange: (file: UploadFile | null) => void;
}

const PhotoCapturePage: React.FC<PhotoCapturePageProps> = ({
  uploadedPhoto,
  onPhotoChange,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>(
    uploadedPhoto ? [uploadedPhoto] : [],
  );

  const customValidateFile = async (file: File): Promise<File | null> => {
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      message.error(t("selfSignIn.photo.invalidFormat"));
      return null;
    }

    if (file.size > 1048576) {
      // 1MB
      return await resizeImage(file, 600, 0.7);
    }

    return file;
  };

  const customBeforeUpload = async (file: File) => {
    const maxSize = 10485760; // 10MB
    if (file.size > maxSize) {
      message.error(t("selfSignIn.photo.fileTooLarge"));
      return Upload.LIST_IGNORE;
    }

    const validFile = await customValidateFile(file);
    return validFile ? false : Upload.LIST_IGNORE;
  };

  const handleChange: UploadProps["onChange"] = async (info) => {
    if (info.file.status === "removed") {
      setFileList([]);
      onPhotoChange(null);
      return;
    }

    if (info.fileList[0]?.originFileObj) {
      const validFile = await customValidateFile(
        info.fileList[0].originFileObj,
      );
      if (validFile) {
        // Create a new UploadFile object with the validated file
        const uploadFile: UploadFile = {
          uid: info.fileList[0].uid,
          name: validFile.name,
          status: "done",
          originFileObj: validFile as any,
        };
        setFileList([uploadFile]);
        onPhotoChange(uploadFile);
      }
    }
  };

  return (
    <div className="workflow-container">
      <div className="message">
        {t("selfSignIn.photo.uploadPrompt")}
        <br />
        {t("selfSignIn.photo.requirements")}
      </div>

      <div className="photo-upload-container">
        <Upload
          listType="picture"
          fileList={fileList}
          beforeUpload={customBeforeUpload}
          onChange={handleChange}
          showUploadList={{ showPreviewIcon: true }}
          maxCount={1}
          previewFile={async (file) => {
            return URL.createObjectURL(file); // Create a local preview URL
          }}
        >
          {fileList.length === 0 && (
            <Button icon={<UploadOutlined />}>
              {t("selfSignIn.photo.uploadButton")}
            </Button>
          )}
        </Upload>
      </div>
    </div>
  );
};

export default PhotoCapturePage;
